import { useCallback } from "react";

import { createReference } from "@medplum/core";
import { Reference, Resource, ResourceType } from "@medplum/fhirtypes";
import { AsyncAutocomplete, AsyncAutocompleteProps, useMedplum } from "@medplum/react";

import { TextInput } from "@mantine/core";

import { CreatableReferenceId } from "@/fhir/enums/fhir-valuesets";
import { referenceToOption } from "@/utils/autocomplete";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

interface QuestionnaireReferenceFieldProps<T extends Reference<Resource>>
  extends Omit<AsyncAutocompleteProps<T>, "toOption" | "loadOptions" | "defaultValue"> {
  defaultValue: T[] | undefined;
  resourceType: ResourceType;
  creatable?: boolean;
  searchCriteria: string;
  isLoading?: boolean;
}

export default function QuestionnaireReferenceField<T extends Reference<Resource>>({
  defaultValue,
  onChange,
  resourceType,
  searchCriteria,
  creatable,
  isLoading,
  ...props
}: QuestionnaireReferenceFieldProps<T>) {
  const medplum = useMedplum();

  const loadOptions = useCallback(
    async (query: string) => {
      try {
        const search = JSON.parse(searchCriteria.replaceAll("${query}", query));
        const { bundle } = await medplum.searchResources(resourceType, search);
        return getResourcesFromBundle(resourceType, bundle).map(createReference) as T[];
      } catch (error) {
        showSystemErrorNotification();
        throw error;
      }
    },
    [medplum, resourceType, searchCriteria],
  );

  const handleCreate = useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) {
        return undefined;
      }

      return {
        reference: joinReferenceString(resourceType, CreatableReferenceId.Custom),
        display: trimmedQuery,
      } as T;
    },
    [resourceType],
  );

  const toOption = useCallback((reference: T) => {
    if (!reference.display?.trim()) {
      return undefined;
    }

    return referenceToOption(reference);
  }, []);

  if (isLoading) {
    return <TextInput disabled label={props.label} placeholder="Loading..." />;
  }

  return (
    <AsyncAutocomplete<T>
      maxValues={1}
      label={props.label}
      onChange={onChange}
      creatable={creatable}
      loadOptions={loadOptions}
      defaultValue={defaultValue}
      placeholder={props.placeholder}
      onCreate={handleCreate as (input: string) => T}
      toOption={toOption as (item: T) => ReturnType<typeof referenceToOption<T>>}
      {...props}
    />
  );
}
